/**
 * `ChatFab` — floating BuildIntel Assistant sheet with streaming chat AND
 * (new in iter46) voice input via the reusable `useVoiceCapture` engine.
 *
 * Voice flow:
 *   idle    → tap mic → recording (mic turns red, timer shown)
 *   recording → tap mic → uploading (input shows "Transcribing…")
 *   uploading → parsed
 *     • Transcript always drops into the input box for review/edit before
 *       the tradie sends it as a chat message.
 *     • If the parse also looks like a real expense (total > 0 AND
 *       supplier), an inline "Log as expense" chip appears above the
 *       input. Tapping the chip navigates to /review with the parsed
 *       docket — reusing the existing confirmation flow so accountability
 *       stays consistent.
 *
 * Text chat is untouched — the voice path is a strict addition. Existing
 * testids (`chat-fab`, `chat-close`, `chat-input`, `chat-send`,
 * `chat-msg-*`, `chat-disclaimer`) are preserved for iter29+ E2E anchors.
 */

import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type KeyboardEvent as ReactKeyboardEvent,
  type ChangeEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { Send, X, Sparkles, Mic, Square, Radio, Receipt, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import {
  Sheet,
  SheetContent as SheetContentJs,
  SheetHeader as SheetHeaderJs,
  SheetTitle as SheetTitleJs,
  SheetDescription as SheetDescriptionJs,
} from "@/components/ui/sheet";
import DisclaimerJs from "@/components/Disclaimer";
import { useVoiceCapture, type VoiceParseResult } from "@/agent/voice";
import { formatMoney } from "@/shared/format";

/* ------------------------------------------------------------------ */
/* JS interop shims — same pattern as History.tsx / VoiceRecorder.tsx  */
/* ------------------------------------------------------------------ */

const SheetContent = SheetContentJs as unknown as React.FC<
  React.PropsWithChildren<{ side?: string; className?: string }>
>;
const SheetHeader = SheetHeaderJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const SheetTitle = SheetTitleJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const SheetDescription = SheetDescriptionJs as unknown as React.FC<
  React.PropsWithChildren<{ className?: string }>
>;
const Disclaimer = DisclaimerJs as unknown as React.FC<{
  testid?: string;
  text: string;
}>;

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

interface Msg {
  id: string;
  role: "assistant" | "user";
  content: string;
}

// Starter chips — one-tap prompts for the most common tradie asks. Only
// shown until the tradie has had their first assistant reply so the
// suggestions don't clutter an active conversation.
const STARTER_CHIPS: { id: string; label: string; prompt: string }[] = [
  { id: "gst", label: "Am I registered for GST?", prompt: "Am I registered for GST and how much have I claimed this quarter?" },
  { id: "ute", label: "How do I claim my ute?", prompt: "How do I claim my ute — cents-per-km or logbook?" },
  { id: "wfh", label: "Home office claim", prompt: "What can I claim for my home office this year?" },
  { id: "bas", label: "Is my BAS ready?", prompt: "Am I ready to send my BAS this quarter? Show me what's good and what's missing." },
  { id: "flags", label: "What flags do I have?", prompt: "What smart flags do I have right now, and how do I fix them?" },
  { id: "receipts", label: "Missing receipts?", prompt: "Which entries are missing receipts and where do I fix them?" },
];

const uid = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Known in-app routes the assistant may suggest — kept in sync with the
// backend APP_ROUTE_MAP so a bare `/vehicle-wfh` reference is rendered as
// a tappable chip inside the chat bubble (Module 5 — Stronger Assistant).
const APP_ROUTE_LABELS: Record<string, string> = {
  "/capture": "Capture",
  "/voice": "Voice log",
  "/queue": "Review queue",
  "/history": "Ledger",
  "/jobs": "Jobs",
  "/tax": "Tax AI",
  "/personal-tax": "Personal tax",
  "/vehicle-wfh": "Vehicle & WFH",
  "/ohs": "Take 5 safety",
  "/profile": "Profile",
};
const APP_ROUTE_REGEX = new RegExp(
  `(${Object.keys(APP_ROUTE_LABELS)
    .sort((a, b) => b.length - a.length) // longest match first
    .map((r) => r.replace(/\//g, "\\/"))
    .join("|")})(?![a-z0-9_-])`,
  "g"
);

const fmtTime = (s: number): string =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/** An expense-shaped parse has BOTH a positive total AND a supplier — a
 * transcript that's a real cost, not just a question. */
const looksLikeExpense = (p: VoiceParseResult): boolean =>
  typeof p.total === "number" &&
  p.total > 0 &&
  typeof p.supplier === "string" &&
  p.supplier.trim().length > 0;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

// Flag-phrase patterns — when the assistant says "3 entries missing
// receipts" we inject a "Show these" chip that deep-links to /history
// filtered by that flag key. Regex is intentionally forgiving to
// wording variants ("without a receipt", "no receipts on file", etc.).
const FLAG_PHRASE_MATCHERS: Array<{ key: string; label: string; re: RegExp }> = [
  {
    key: "no_receipt",
    label: "Show missing receipts",
    re: /(\d+)\s+entr(?:y|ies|y\/entries)?\s+(?:≥\s*\$?\d+(?:\.\d+)?\s+)?(?:without|missing|with\s+no|no)\s+(?:a\s+)?receipts?/i,
  },
  {
    key: "missing_abn_subcontractor",
    label: "Show subbies missing ABN",
    re: /(\d+)\s+subcontractors?\s+(?:with\s+)?(?:missing|no|without)\s+(?:an?\s+)?ABN/i,
  },
  {
    key: "unassigned_job",
    label: "Show unassigned entries",
    re: /(\d+)\s+entr(?:y|ies|y\/entries)?\s+(?:not\s+linked|without\s+a\s+link|unassigned)(?:\s+to\s+a\s+job)?/i,
  },
  {
    key: "gst_mismatch",
    label: "Show GST mismatches",
    re: /(\d+)\s+GST\s+mismatch(?:es)?/i,
  },
];

/**
 * Scan a slice of assistant text and yield an array of {index, length,
 * node} match tokens for both route mentions AND flag phrases. Callers
 * merge these with the surrounding text into the final react tree.
 */
type Token = { index: number; length: number; node: React.ReactNode };

function scanTokens(text: string, onNav: (path: string) => void): Token[] {
  const tokens: Token[] = [];

  // Strip markdown emphasis before scanning so bolded counts like
  // "**10 entries** without a receipt" still match the FLAG_PHRASE
  // patterns. We keep the ORIGINAL text for character offsets by
  // building a mirror string of the same length where the emphasis
  // punctuation (**, __, *, _) is replaced with spaces — that way
  // scan indices land on the correct position in the surviving text.
  const scanText = text.replace(/(\*\*|__|\*|_)/g, (m) => " ".repeat(m.length));

  // 1) In-app route mentions → tappable chip (Module 5)
  APP_ROUTE_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = APP_ROUTE_REGEX.exec(scanText)) !== null) {
    const path = m[0];
    const label = APP_ROUTE_LABELS[path] || path;
    tokens.push({
      index: m.index,
      length: path.length,
      node: (
        <button
          key={`route-${path}-${m.index}`}
          type="button"
          data-testid={`chat-path-link-${path.replace(/\//g, "-")}`}
          onClick={() => onNav(path)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md bg-[#FF9500]/15 border border-[#FF9500]/40 text-[#FF9500] font-heading font-bold text-[10px] uppercase tracking-wider hover:bg-[#FF9500]/25 transition align-baseline"
        >
          {label}
        </button>
      ),
    });
  }

  // 2) Flag phrases → "Show these" deep-link chip appended after the
  // matched phrase (Assistant Deep Actions). We emit the phrase itself
  // untouched then a zero-width token immediately after for the chip.
  for (const { key, label, re } of FLAG_PHRASE_MATCHERS) {
    const rex = new RegExp(re.source, re.flags.includes("g") ? re.flags : re.flags + "g");
    let fm: RegExpExecArray | null;
    while ((fm = rex.exec(scanText)) !== null) {
      const endIdx = fm.index + fm[0].length;
      tokens.push({
        index: endIdx,
        length: 0,
        node: (
          <span key={`flag-wrap-${key}-${fm.index}`}>
            {" "}
            <button
              key={`flag-${key}-${fm.index}`}
              type="button"
              data-testid={`chat-flag-jump-${key}`}
              onClick={() => onNav(`/history?flag=${key}`)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FBBF24]/15 border border-[#FBBF24]/45 text-[#FBBF24] font-heading font-bold text-[10px] uppercase tracking-wider hover:bg-[#FBBF24]/25 transition align-baseline"
            >
              → {label}
            </button>
          </span>
        ),
      });
    }
  }

  tokens.sort((a, b) => a.index - b.index || a.length - b.length);
  return tokens;
}

/**
 * Render an assistant message body, replacing any bare `/route` reference
 * with a tappable chip (Module 5) AND appending a "Show these" chip
 * after any flag-phrase mention (Assistant Deep Actions).
 */
function renderAssistantBody(
  text: string,
  onNav: (path: string) => void
): React.ReactNode {
  const tokens = scanTokens(text, onNav);
  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (const t of tokens) {
    if (t.index > cursor) out.push(text.slice(cursor, t.index));
    out.push(t.node);
    cursor = t.index + t.length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

export default function ChatFab() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([
    {
      id: uid(),
      role: "assistant",
      content:
        "G'day! I'm BuildIntel AI. Ask me about GST, BAS, deductions, or tap the mic and speak — I can log expenses too.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string>(() => uid());
  const [busy, setBusy] = useState(false);
  const [expenseHint, setExpenseHint] = useState<VoiceParseResult | null>(null);
  // `voiceRetry` toggles the "Tap to retry voice" chip. Set on failure, cleared
  // when the tradie starts a new recording, types anything, or a fresh
  // transcript arrives successfully. Never re-set inside the effect except in
  // the catch branch (once per failed blob, guaranteed by `processedBlobRef`).
  const [voiceRetry, setVoiceRetry] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const voice = useVoiceCapture();

  // Surface mic-permission errors once each.
  useEffect(() => {
    if (voice.error) toast.error(voice.error);
  }, [voice.error]);

  // Listen for a global "open the assistant with this prompt" event so
  // other screens (FY Summary card, pack share row) can trigger the
  // pack-coach without having to render their own chat sheet.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail || {};
      setOpen(true);
      if (detail.prompt) {
        // Give the sheet a tick to mount before firing the prompt so the
        // streaming message doesn't race the animation.
        setTimeout(() => { void send(detail.prompt as string); }, 200);
      }
    };
    window.addEventListener("buildintel:open-chat", handler as EventListener);
    return () => window.removeEventListener("buildintel:open-chat", handler as EventListener);
    // We deliberately depend on `send` so the callback we register always
    // has the latest closure of the outer state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [msgs, busy]);

  /* ---------------------------- Text chat --------------------------- */

  const send = useCallback(
    async (overrideText?: string) => {
      const text = (overrideText ?? input).trim();
      if (!text || busy) return;
      setInput("");
      setExpenseHint(null);
      const userMsgId = uid();
      const assistantMsgId = uid();
      setMsgs((m) => [
        ...m,
        { id: userMsgId, role: "user", content: text },
        { id: assistantMsgId, role: "assistant", content: "" },
      ]);
      setBusy(true);
      try {
        const resp = await fetch(`${API_BASE}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ message: text, session_id: sessionId }),
        });
        if (!resp.ok || !resp.body) throw new Error("chat failed");
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let idx: number;
          while ((idx = buf.indexOf("\n\n")) !== -1) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            if (chunk.startsWith("data: ")) {
              try {
                const j = JSON.parse(chunk.slice(6));
                if (j.delta) {
                  setMsgs((m) =>
                    m.map((mm) =>
                      mm.id === assistantMsgId
                        ? { ...mm, content: mm.content + j.delta }
                        : mm
                    )
                  );
                }
                if (j.session_id) setSessionId(j.session_id);
              } catch (err) {
                devLog.warn("chat parse error", err);
              }
            }
          }
        }
      } catch (err) {
        devLog.error("chat stream failed", err);
        setMsgs((m) =>
          m.map((mm) =>
            mm.id === assistantMsgId
              ? { ...mm, content: "Sorry, I couldn't reach the AI just now." }
              : mm
          )
        );
      } finally {
        setBusy(false);
      }
    },
    [input, busy, sessionId]
  );

  /* ---------------------------- Voice input ------------------------- */

  const startVoice = useCallback(() => {
    setExpenseHint(null);
    setVoiceRetry(false);
    void voice.start();
  }, [voice]);

  // The voice engine returns a fresh object every render — never include
  // `voice` in a dep array or you'll re-fire this effect on every parent
  // state change. Instead we mirror the latest engine into a ref, and use
  // the current preview *blob identity* as the dedup key so each recording
  // is uploaded EXACTLY ONCE regardless of re-renders.
  const voiceRef = useRef(voice);
  useEffect(() => {
    voiceRef.current = voice;
  });

  const processedBlobRef = useRef<Blob | null>(null);

  useEffect(() => {
    const currentBlob = voice.preview?.blob ?? null;
    if (voice.status !== "previewing" || !currentBlob) return;
    if (processedBlobRef.current === currentBlob) return; // already handled
    processedBlobRef.current = currentBlob;

    // NOTE: we intentionally do NOT gate on a `cancelled` flag here.
    // `voice.upload()` synchronously flips `voice.status` from `previewing`
    // → `uploading`, which re-runs this effect and fires its cleanup —
    // so a naive `cancelled` guard would swallow BOTH the success and
    // failure paths for every attempt. Instead we use `processedBlobRef`
    // as the freshness check: if the tradie records a NEWER blob mid-
    // flight, `processedBlobRef.current` moves on and this attempt's
    // results are ignored below.
    const attemptBlob = currentBlob;
    const isStale = () => processedBlobRef.current !== attemptBlob;

    (async () => {
      try {
        const parsed = await voiceRef.current.upload();
        if (isStale()) return; // a newer recording superseded this one
        const transcript = (parsed.raw_transcript || "").trim();
        if (transcript) setInput(transcript);
        if (looksLikeExpense(parsed)) setExpenseHint(parsed);
        setVoiceRetry(false);
      } catch (err) {
        if (isStale()) return;
        devLog.warn("chat voice transcribe failed", err);
        // Sonner de-dupes by `id`, so residual duplicate attempts still
        // surface exactly ONE banner to the tradie.
        toast.error("Voice didn't come through. Please try again or type.", {
          id: "chat-voice-err",
        });
        // Surface the persistent "Tap to retry" chip so a tradie who
        // missed the transient toast still has an obvious recovery path.
        setVoiceRetry(true);
        // Discard the failed preview so the guard clause at the top of
        // this effect returns early on any subsequent render — no retry
        // storms even if the engine flips status again.
        voiceRef.current.discard();
      }
    })();
  }, [voice.status, voice.preview]);

  const logExpense = useCallback(() => {
    if (!expenseHint) return;
    const parsed = expenseHint;
    setExpenseHint(null);
    setInput("");
    setOpen(false);
    nav("/review", {
      state: {
        parsed,
        source: "voice",
        supplierSuggestions: [] as string[],
      },
    });
  }, [expenseHint, nav]);

  /* ---------------------------- UI helpers ------------------------- */

  const isRecording = voice.status === "recording";
  const isUploading = voice.status === "uploading";
  const inputPlaceholder = isRecording
    ? `Recording · ${fmtTime(voice.elapsedSec)}`
    : isUploading
      ? "Transcribing…"
      : "Ask, or tap the mic to speak…";

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") void send();
  };

  return (
    <>
      <button
        data-testid="chat-fab"
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-[var(--fab-bottom,5.5rem)] z-40 w-14 h-14 rounded-full bi-accent-btn shadow-[0_0_0_1px_rgba(255,149,0,0.4)] flex items-center justify-center transition-[bottom] duration-200"
      >
        <Sparkles className="w-6 h-6" />
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-white/10 text-white h-[80vh] p-0 flex flex-col [&>button.absolute]:hidden"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>BuildIntel Assistant</SheetTitle>
            <SheetDescription>
              Ask questions about GST, BAS, deductions, or anything trade
              accounting related. Tap the mic to speak.
            </SheetDescription>
          </SheetHeader>
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#FF9500] flex items-center justify-center text-[#111827]">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-bold">BuildIntel Assistant</div>
                <div
                  data-testid="chat-header-brand"
                  className="text-[11px] text-white/50"
                >
                  Powered by BuildIntel AI
                </div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              className="w-10 h-10 rounded-full bg-[#2A2A2A] border border-white/10 text-white/70 hover:text-[#FF9500] hover:border-[#FF9500]/40 active:scale-95 transition flex items-center justify-center appearance-none"
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              <X className="w-[18px] h-[18px]" strokeWidth={2.25} />
            </button>
          </div>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          >
            {msgs.map((m, i) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`max-w-[85%] whitespace-pre-wrap text-sm rounded-2xl px-3.5 py-2.5 ${
                    m.role === "user"
                      ? "bg-[#FF9500] text-[#111827] font-medium"
                      : "bi-surface text-white"
                  }`}
                >
                  {m.content
                    ? m.role === "assistant"
                      ? renderAssistantBody(m.content, (path) => {
                          setOpen(false);
                          nav(path);
                        })
                      : m.content
                    : busy && i === msgs.length - 1
                    ? <span className="opacity-60">Thinking…</span>
                    : null}
                </div>
              </div>
            ))}
          </div>

          {/* Inline "Tap to retry voice" chip — appears ONLY after a failed
              transcription. Dismisses on its own tap (retries the mic flow),
              or on any typing / successful transcription upstream. Sits
              above the disclaimer so a tradie who missed the transient
              error toast still has an obvious, persistent recovery path. */}
          {voiceRetry && !isRecording && !isUploading && (
            <div className="px-4 pb-1">
              <button
                type="button"
                data-testid="chat-voice-retry-chip"
                onClick={() => {
                  setVoiceRetry(false);
                  toast.dismiss("chat-voice-err");
                  startVoice();
                }}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#FF9500]/40 bg-[#FF9500]/10 px-3 py-2.5 text-left hover:brightness-110 active:scale-[0.995] transition"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <RotateCcw className="w-4 h-4 text-[#FF9500] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF9500] font-heading">
                      Voice didn&rsquo;t come through
                    </div>
                    <div className="text-xs text-white truncate">
                      Tap to try voice again
                    </div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#FF9500] font-heading font-bold shrink-0">
                  Retry →
                </span>
              </button>
            </div>
          )}

          {/* Inline "Log as expense" chip — only appears when the voice
              parse looks like a real cost. Reuses /review as the single
              source-of-truth confirmation flow. */}
          {expenseHint && (
            <div className="px-4 pb-1">
              <button
                type="button"
                data-testid="chat-log-expense-chip"
                onClick={logExpense}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#FF9500]/40 bg-[#FF9500]/10 px-3 py-2.5 text-left hover:brightness-110 transition"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Receipt className="w-4 h-4 text-[#FF9500] shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[#FF9500] font-heading">
                      Sounds like an expense
                    </div>
                    <div
                      data-testid="chat-log-expense-line"
                      className="text-xs text-white truncate"
                    >
                      {expenseHint.confirmation_line ||
                        `${formatMoney(expenseHint.total)} at ${expenseHint.supplier}`}
                    </div>
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#FF9500] font-heading font-bold shrink-0">
                  Log →
                </span>
              </button>
            </div>
          )}

          {/* Starter chips — tap-and-go prompts. Auto-hide once the
              tradie has had their first back-and-forth so a busy chat
              doesn't get spammed with starter suggestions. */}
          {msgs.filter((m) => m.role === "assistant" && m.content).length <= 1 && !busy && (
            <div
              className="px-4 pt-2 flex flex-wrap gap-1.5"
              data-testid="chat-starter-chips"
            >
              {STARTER_CHIPS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`chat-chip-${c.id}`}
                  onClick={() => void send(c.prompt)}
                  disabled={busy}
                  className="px-2.5 py-1 rounded-full border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-[11px] text-white/85 font-heading disabled:opacity-60 transition"
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          <div className="px-4 pt-2 space-y-1">
            <Disclaimer
              testid="chat-disclaimer"
              text="Helper tool only — not a registered tax agent. Confirm figures with your accountant before lodgement."
            />
            <div
              data-testid="chat-powered-by"
              className="text-[9px] uppercase tracking-[0.24em] text-white/30 font-heading text-center"
            >
              Powered by BuildIntel AI
            </div>
          </div>
          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <button
              type="button"
              data-testid="chat-mic-btn"
              onClick={isRecording ? voice.stop : startVoice}
              disabled={isUploading}
              className={`h-14 w-14 rounded-xl flex items-center justify-center transition disabled:opacity-50 shrink-0 ${
                isRecording
                  ? "bg-[#F87171] text-[#111827] shadow-[0_10px_30px_-12px_rgba(248,113,113,0.7)]"
                  : "bi-ghost-btn text-white/80 hover:text-white"
              }`}
              aria-label={isRecording ? "Stop recording" : "Speak"}
            >
              {isRecording ? (
                <Square className="w-5 h-5" fill="currentColor" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              {isRecording && (
                <Radio
                  data-testid="chat-mic-live"
                  className="w-3 h-3 absolute -top-1 -right-1 text-[#F87171] animate-pulse"
                />
              )}
            </button>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setInput(e.target.value);
                if (voiceRetry) setVoiceRetry(false);
              }}
              onKeyDown={onKeyDown}
              placeholder={inputPlaceholder}
              disabled={isRecording || isUploading}
              className="flex-1 bi-input"
            />
            <button
              data-testid="chat-send"
              onClick={() => void send()}
              disabled={busy || isRecording || isUploading}
              className="h-14 w-14 rounded-xl bi-accent-btn flex items-center justify-center disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
